/**
 * LearnWorkspacePage — LitHub workspace at `/library/shelf` (legacy `/learn` supported)
 * 
 * Redesigned with expandable card layout:
 * - Digest filter bar at top (SharedDigestFilterBar) — articles filtered by selected digest
 * - Flat article list (no folder accordion)
 * - Card per article: title + metadata, truncated abstract, truncated AI summary
 * - Action buttons: Audio Summary, Deep Dive, Discuss, Full Text
 * - Articles sorted newest-to-oldest by default
 */
import React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../components/workspace/WorkspaceShell';
import { libraryAPI, articlesAPI, preferencesAPI } from '../../api';
import { toast } from 'sonner';
import LibraryArticleCard from '../../components/LibraryArticleCard';
import { SharedDigestFilterBar, type DigestProfileStat } from '../../components/shared/SharedDigestFilterBar';
import { LitHubViewTabs } from '../../components/lithub/LitHubViewTabs';
import { LitArchiveTable } from '../../components/lithub/LitArchiveTable';
import { FolderSelectDialog } from '../../components/workspace/FolderSelectDialog';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { navV2Routes } from '../../hooks/useNavV2';
import { LITARCHIVE_PATH, LITHUB_SHELF_PATH } from '../../config/featureNav';
import type { LibraryArticleRecord } from '../../types/libraryProvenance';
import { itemMatchesDigestProfile } from '../../utils/digestArticleMatch';
import {
  getPersistedDigestProfileId,
  setPersistedDigestProfileId,
} from '../../utils/sharedDigestSelection';
import { mapApiArticleToRecord } from '../../utils/sourcePermissions';
import { getSavedViews, createSavedView, renameSavedView, deleteSavedView, SavedView } from '../../config/savedViews';
import { articleMetadataAPI } from '../../api';
import {
  getGoal, setGoal as saveGoal, clearGoal, appendReadLog, computeAnalytics,
  type ReadingGoal, type GoalPeriod, type ProgressAnalytics,
} from '../../config/readingGoals';
import {
  trackPageView,
  trackLitScholarOpened,
} from '../../utils/analytics';
import {
  Search,
  BookOpen,
  X,
  Bookmark,
  Save,
  Trash2,
  Pencil,
  FolderPlus,
  Plus,
  Headphones,
  Brain,
} from 'lucide-react';

interface LibraryArticle extends LibraryArticleRecord {}

function formatDigestRunTime(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return null;
  }
}

/** Match library articles to a digest profile. */
function articleMatchesDigestProfile(
  article: LibraryArticleRecord,
  profile: DigestProfileStat
): boolean {
  return itemMatchesDigestProfile(article, profile);
}

const LearnWorkspacePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshBootstrap } = useWorkspace();
  const wsUserId =
    (useWorkspace()?.bootstrap as any)?.user_id || user?.user_id || 'anon';
  const navV2Enabled = useFeatureFlag('enable_navigation_v2');

  // Route params for Nav V2 (read from URL — URL is source of truth)
  const routeParams = useParams<{ profileId?: string; pmid?: string }>();
  const location = useLocation();
  const litHubRoutePrefix = location.pathname.startsWith('/learn') ? '/learn' : '/library';
  const litHubShelf = location.pathname.startsWith('/learn') ? '/learn' : LITHUB_SHELF_PATH;
  const isArchiveView = location.pathname.startsWith('/library/archive') || location.pathname.startsWith('/learn/archive');
  const isDigestRoute = navV2Enabled && location.pathname.includes(`${litHubRoutePrefix}/digests/`);
  const isArticleRoute = navV2Enabled && location.pathname.includes(`${litHubRoutePrefix}/articles/`);

  const [articles, setArticles] = React.useState<LibraryArticle[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [totalCount, setTotalCount] = React.useState(0);
  
  // Digest filter state — restore across apps; URL wins when Nav V2 has a digest route
  const [selectedProfileId, setSelectedProfileId] = React.useState<string | null>(
    () => getPersistedDigestProfileId()
  );

  const [folderDialogOpen, setFolderDialogOpen] = React.useState(false);
  const [folderTarget, setFolderTarget] = React.useState<LibraryArticle | null>(null);
  const [profileStats, setProfileStats] = React.useState<DigestProfileStat[]>([]);
  const [profileStatsLoading, setProfileStatsLoading] = React.useState(true);

  const loadProfileStats = React.useCallback(async () => {
    try {
      setProfileStatsLoading(true);
      const response = await preferencesAPI.getProfilesScreenStats();
      setProfileStats(response.profiles || []);
    } catch (error) {
      console.error('Failed to load profile stats:', error);
    } finally {
      setProfileStatsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadProfileStats();
  }, [loadProfileStats]);

  const selectedProfileStats = React.useMemo(
    () => profileStats.find((p) => p.profile_id === selectedProfileId),
    [profileStats, selectedProfileId]
  );

  const lastDigestRunLabel = formatDigestRunTime(selectedProfileStats?.last_digest_date);

  // Nav V2: Sync profileId from route params on every URL change
  React.useEffect(() => {
    if (!navV2Enabled) return;
    if (isDigestRoute && routeParams.profileId) {
      setSelectedProfileId(routeParams.profileId);
      setPersistedDigestProfileId(routeParams.profileId);
    } else if (isArticleRoute) {
      // In article mode, clear profile filter to maximize finding the article
      setSelectedProfileId(null);
      setSearchQuery('');
    } else if (!isDigestRoute && !isArticleRoute) {
      // Root LitHub — restore last digest across apps (do not force "All")
      const persisted = getPersistedDigestProfileId();
      if (persisted) {
        setSelectedProfileId(persisted);
        navigate(`${litHubRoutePrefix}/digests/${persisted}`, { replace: true });
      }
    }
  }, [navV2Enabled, isDigestRoute, isArticleRoute, routeParams.profileId, location.pathname]);

  // Nav V2: Article focus — scroll to and highlight target PMID
  const [focusedPmid, setFocusedPmid] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!navV2Enabled || !isArticleRoute || !routeParams.pmid) return;
    setFocusedPmid(routeParams.pmid);
  }, [navV2Enabled, isArticleRoute, routeParams.pmid, location.pathname]);

  // Scroll to focused article after articles load
  React.useEffect(() => {
    if (!focusedPmid || loading) return;
    const timeout = setTimeout(() => {
      const el = document.querySelector(`[data-testid="library-card-${focusedPmid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-2', 'ring-sky-400', 'ring-offset-2');
        setTimeout(() => el.classList.remove('ring-2', 'ring-sky-400', 'ring-offset-2'), 3000);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [focusedPmid, loading, articles]);

  // Nav V2: Route-aware chip click handler
  const handleProfileSelect = React.useCallback((id: string | null) => {
    setPersistedDigestProfileId(id);
    if (navV2Enabled) {
      navigate(id ? `${litHubRoutePrefix}/digests/${id}` : litHubShelf);
    } else {
      setSelectedProfileId(id);
    }
  }, [navV2Enabled, navigate, litHubRoutePrefix, litHubShelf]);

  // Audio state
  const [audioStatuses, setAudioStatuses] = React.useState<Record<string, { status: string; audio_url?: string }>>({});
  const [audioPlayerPmid, setAudioPlayerPmid] = React.useState<string | null>(null);
  const [audioPlayerUrl, setAudioPlayerUrl] = React.useState<string | null>(null);

  // Read tracking — mark articles as "read" when user expands abstract or AI summary
  const [readArticles, setReadArticles] = React.useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('lithub_read_articles');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });

  // Reading goals state — declared before markAsRead so it can reference them
  const goalsEnabled = useFeatureFlag('enable_reading_goals');
  const goalsUserId = wsUserId;
  const [analytics, setAnalytics] = React.useState<ProgressAnalytics | null>(null);
  const [showGoalEditor, setShowGoalEditor] = React.useState(false);
  const [goalTarget, setGoalTarget] = React.useState(5);
  const [goalPeriod, setGoalPeriod] = React.useState<GoalPeriod>('week');

  React.useEffect(() => {
    if (goalsEnabled && goalsUserId) {
      const a = computeAnalytics(goalsUserId);
      setAnalytics(a);
      if (a.goal) {
        setGoalTarget(a.goal.target);
        setGoalPeriod(a.goal.period);
      }
    }
  }, [goalsEnabled, goalsUserId]);

  const markAsRead = React.useCallback((pmid: string) => {
    setReadArticles(prev => {
      if (prev.has(pmid)) return prev;
      const next = new Set(prev);
      next.add(pmid);
      localStorage.setItem('lithub_read_articles', JSON.stringify([...next]));
      return next;
    });
    // Append to read log for goals/streaks (flag-gated)
    if (goalsEnabled && goalsUserId) {
      appendReadLog(goalsUserId, pmid);
      setAnalytics(computeAnalytics(goalsUserId));
    }
  }, [goalsEnabled, goalsUserId]);

  // Saved views — flag-gated, localStorage-backed
  const savedViewsEnabled = useFeatureFlag('enable_saved_views');
  const userId = wsUserId;
  const [savedViews, setSavedViews] = React.useState<SavedView[]>([]);
  const [activeViewId, setActiveViewId] = React.useState<string | null>(null);
  const [showSaveInput, setShowSaveInput] = React.useState(false);
  const [newViewName, setNewViewName] = React.useState('');
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState('');

  React.useEffect(() => {
    if (savedViewsEnabled) setSavedViews(getSavedViews(userId, 'lithub'));
  }, [savedViewsEnabled, userId]);

  const handleSaveView = () => {
    if (!newViewName.trim()) return;
    const view = createSavedView(userId, 'lithub', newViewName.trim(), {
      profileId: selectedProfileId,
      searchQuery: searchQuery,
    });
    setSavedViews(getSavedViews(userId, 'lithub'));
    setActiveViewId(view.id);
    setNewViewName('');
    setShowSaveInput(false);
    toast.success(`View "${view.name}" saved`);
  };

  const handleSelectView = (view: SavedView) => {
    setSelectedProfileId(view.filters.profileId || null);
    setSearchQuery(view.filters.searchQuery || '');
    setActiveViewId(view.id);
  };

  const handleClearView = () => {
    setActiveViewId(null);
    setSelectedProfileId(null);
    setSearchQuery('');
  };

  const handleDeleteView = (viewId: string) => {
    deleteSavedView(userId, 'lithub', viewId);
    setSavedViews(getSavedViews(userId, 'lithub'));
    if (activeViewId === viewId) setActiveViewId(null);
    toast.success('View deleted');
  };

  const handleRenameView = (viewId: string) => {
    if (!renameValue.trim()) return;
    renameSavedView(userId, 'lithub', viewId, renameValue.trim());
    setSavedViews(getSavedViews(userId, 'lithub'));
    setRenamingId(null);
    setRenameValue('');
  };

  // Collections state — for article metadata feature (flag-gated)
  const notesEnabled = useFeatureFlag('enable_article_notes');
  const [userCollections, setUserCollections] = React.useState<Array<{ collection_id: string; name: string; article_count: number; created_at: string }>>([]);
  const [newCollectionName, setNewCollectionName] = React.useState('');

  React.useEffect(() => {
    if (notesEnabled) {
      articleMetadataAPI.listCollections().then(setUserCollections).catch(() => {});
    }
  }, [notesEnabled]);

  // Load library function wrapped in useCallback
  const loadLibrary = React.useCallback(async () => {
    try {
      setLoading(true);
      const libraryResponse = await libraryAPI.getLibrary({ limit: 100 });

      const articleList = (libraryResponse.articles || [])
        .filter((a: any): a is typeof a & { pmid: string } => !!a.pmid)
        .map((a: any) => mapApiArticleToRecord(a)) as LibraryArticle[];

      // Sort articles newest-to-oldest by saved_at date
      const sortedArticles = articleList.sort((a, b) => {
        const dateA = a.saved_at ? new Date(a.saved_at).getTime() : 0;
        const dateB = b.saved_at ? new Date(b.saved_at).getTime() : 0;
        return dateB - dateA; // Newest first
      });

      setArticles(sortedArticles);
      setTotalCount(libraryResponse.total || sortedArticles.length);

      // Load audio statuses for all articles
      loadAudioStatuses(sortedArticles.map((a) => a.pmid));
    } catch (error) {
      console.error('Failed to load library:', error);
      toast.error('Failed to load your LitHub');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load library on mount
  React.useEffect(() => {
    trackPageView('learn');
    loadLibrary();
  }, [loadLibrary]);

  const loadAudioStatuses = async (pmids: string[]) => {
    const results = await Promise.allSettled(
      pmids.map(async (pmid) => {
        const status = await articlesAPI.getAudioSummary(pmid);
        return { pmid, status };
      })
    );
    setAudioStatuses((prev) => {
      const next = { ...prev };
      for (const result of results) {
        if (result.status !== 'fulfilled') continue;
        const { pmid, status } = result.value;
        if (status?.status && status.status !== 'missing') {
          next[pmid] = { status: status.status, audio_url: status.audio_url };
        }
      }
      return next;
    });
  };

  // --- Handlers ---
  const handleAudioSummary = async (pmid: string) => {
    setAudioStatuses((prev) => ({ ...prev, [pmid]: { status: 'pending' } }));
    try {
      await articlesAPI.generateAudio(pmid);
      // Poll for completion
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const poll = await articlesAPI.getAudioSummary(pmid);
        if (poll.status === 'ready' && poll.audio_url) {
          setAudioStatuses((prev) => ({ ...prev, [pmid]: { status: 'ready', audio_url: poll.audio_url } }));
          setAudioPlayerPmid(pmid);
          setAudioPlayerUrl(poll.audio_url);
          toast.success('Audio summary ready');
          return;
        }
        if (poll.status === 'failed') throw new Error('failed');
      }
      toast.info('Audio is being generated. Check back in a moment.');
      setAudioStatuses((prev) => ({ ...prev, [pmid]: { status: 'pending' } }));
    } catch {
      setAudioStatuses((prev) => ({ ...prev, [pmid]: { status: 'failed' } }));
      toast.error('Audio generation failed');
    }
  };

  const handleRetryAudio = (pmid: string) => handleAudioSummary(pmid);

  const handleDeepDive = (pmid: string) => {
    trackLitScholarOpened(pmid, 'card_button');
    const article = articles.find((a) => a.pmid === pmid);
    const navState = {
      articles: article ? [article] : [],
      pmids: [pmid],
    };
    if (navV2Enabled) {
      navigate(navV2Routes.articleDetail('litscholar', pmid, selectedProfileId || undefined, 'learn'), {
        state: navState,
      });
    } else {
      navigate('/litscholar', { state: navState });
    }
  };

  const handleDiscuss = (pmid: string) => {
    navigate(`/community/new?pmid=${pmid}`);
  };

  const handleSearchFullText = ({ pmid, title }: { pmid: string; title?: string }) => {
    const params = new URLSearchParams({ pmid });
    if (title?.trim()) params.set('q', title.trim());
    navigate(`/litportal?${params.toString()}`);
  };

  const handleRemoveArticle = async (pmid: string) => {
    try {
      await libraryAPI.removeArticle(pmid);
      setArticles((prev) => prev.filter((a) => a.pmid !== pmid));
      setTotalCount((prev) => prev - 1);
      toast.success('Removed from LitHub');
      refreshBootstrap();
    } catch {
      toast.error('Failed to remove article');
    }
  };

  const handleAddToFolder = (article: LibraryArticle) => {
    setFolderTarget(article);
    setFolderDialogOpen(true);
  };

  const handleConfirmFolder = async (folder: string) => {
    if (!folderTarget?.pmid) return;
    try {
      await libraryAPI.moveToFolder(folderTarget.pmid, folder);
      setArticles((prev) =>
        prev.map((a) => (a.pmid === folderTarget.pmid ? { ...a, folder } : a))
      );
      toast.success(`Moved to "${folder}"`);
    } catch {
      try {
        await libraryAPI.saveArticle({ pmid: folderTarget.pmid, folder, source: 'search' });
        setArticles((prev) =>
          prev.map((a) => (a.pmid === folderTarget.pmid ? { ...a, folder } : a))
        );
        toast.success(`Saved to "${folder}"`);
      } catch {
        toast.error('Could not update folder');
      }
    } finally {
      setFolderTarget(null);
      setFolderDialogOpen(false);
    }
  };

  const handleViewInArchive = () => navigate(LITARCHIVE_PATH);

  const handleOpenOnShelf = (pmid: string) => {
    navigate(`${litHubShelf}?focus=${pmid}`);
  };

  // Filter by digest chip + search
  const filteredArticles = React.useMemo(() => {
    let result = articles;

    if (selectedProfileId) {
      const profile = profileStats.find((p) => p.profile_id === selectedProfileId);
      if (profile) {
        result = result.filter((article) => articleMatchesDigestProfile(article, profile));
      } else {
        result = result.filter((article) => article.profile_id === selectedProfileId);
      }
    }

    if (!searchQuery.trim()) return result;

    const query = searchQuery.toLowerCase();
    return result.filter(
      (article) =>
        article.title?.toLowerCase().includes(query) ||
        article.journal?.toLowerCase().includes(query) ||
        article.ai_summary?.toLowerCase().includes(query) ||
        article.specialty_name?.toLowerCase().includes(query)
    );
  }, [articles, searchQuery, selectedProfileId, profileStats]);

  return (
    <div className="flex flex-col" data-testid="learn-workspace-page">
      <SharedDigestFilterBar
        selectedProfileId={selectedProfileId}
        onSelectProfile={handleProfileSelect}
        profiles={profileStats}
        statsLoading={profileStatsLoading}
        onRefreshStats={loadProfileStats}
        showUnscreenedBadge={false}
        showSavedPercent={true}
        showPrimaryStar={true}
        showRunDigest={false}
        centered={true}
        allLabel="All Digests"
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* Search + digest-filtered articles */}
        <div className="flex-1">
          <div className={isArchiveView ? 'p-4 sm:p-6 lg:p-8' : 'p-4'}>
            <div className={isArchiveView ? 'w-full min-w-0' : 'max-w-4xl mx-auto'}>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <LitHubViewTabs active={isArchiveView ? 'archive' : 'shelf'} />
                {!isArchiveView && totalCount > 0 && (
                  <span className="text-sm text-slate-500">{totalCount} saved article{totalCount !== 1 ? 's' : ''}</span>
                )}
              </div>

              {!isArchiveView && (
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search your library..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setActiveViewId(null); }}
                  className="pl-10 bg-white"
                  data-testid="learn-search-input"
                />
              </div>
              )}

              {!isArchiveView && savedViewsEnabled && (
              <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="saved-views-bar">
                <Bookmark className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                {savedViews.map(v => (
                  <div key={v.id} className="group flex items-center gap-0.5">
                    {renamingId === v.id ? (
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRenameView(v.id); if (e.key === 'Escape') setRenamingId(null); }}
                        onBlur={() => handleRenameView(v.id)}
                        className="px-2 py-0.5 text-xs border rounded w-24"
                        data-testid={`rename-input-${v.id}`}
                      />
                    ) : (
                      <button
                        onClick={() => handleSelectView(v)}
                        className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                          activeViewId === v.id
                            ? 'bg-sky-100 border-sky-300 text-sky-700 font-medium'
                            : 'bg-white border-slate-200 text-slate-600 hover:border-sky-300'
                        }`}
                        data-testid={`saved-view-${v.id}`}
                      >
                        {v.name}
                      </button>
                    )}
                    <button
                      onClick={() => { setRenamingId(v.id); setRenameValue(v.name); }}
                      className="p-0.5 text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Rename"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDeleteView(v.id)}
                      className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Delete"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                {activeViewId && (
                  <button onClick={handleClearView} className="px-2 py-1 text-xs text-slate-400 hover:text-slate-600" data-testid="clear-view">
                    Clear
                  </button>
                )}
                {showSaveInput ? (
                  <div className="flex items-center gap-1">
                    <input
                      autoFocus
                      value={newViewName}
                      onChange={e => setNewViewName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveView(); if (e.key === 'Escape') setShowSaveInput(false); }}
                      placeholder="View name..."
                      className="px-2 py-0.5 text-xs border rounded w-28"
                      data-testid="new-view-name-input"
                    />
                    <button onClick={handleSaveView} className="p-1 text-sky-600 hover:text-sky-700"><Save className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setShowSaveInput(false)} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowSaveInput(true)}
                    className="px-2 py-1 text-xs text-sky-600 hover:text-sky-700 font-medium"
                    data-testid="save-view-btn"
                  >
                    + Save View
                  </button>
                )}
              </div>
              )}

              {!isArchiveView && notesEnabled && (
              <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="collections-bar">
                <FolderPlus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                {userCollections.map(col => (
                  <div key={col.collection_id} className="group flex items-center gap-0.5">
                    <span className="px-2 py-0.5 text-xs bg-sky-50 border border-sky-200 text-sky-700 rounded-full">
                      {col.name} <span className="text-sky-400">({col.article_count})</span>
                    </span>
                    <button onClick={async () => {
                      await articleMetadataAPI.deleteCollection(col.collection_id);
                      setUserCollections(prev => prev.filter(c => c.collection_id !== col.collection_id));
                      toast.success('Collection deleted');
                    }} className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-1">
                  <input
                    value={newCollectionName}
                    onChange={e => setNewCollectionName(e.target.value)}
                    onKeyDown={async e => {
                      if (e.key === 'Enter' && newCollectionName.trim()) {
                        const col = await articleMetadataAPI.createCollection(newCollectionName.trim());
                        setUserCollections(prev => [...prev, { ...col, article_count: 0, created_at: new Date().toISOString() }]);
                        setNewCollectionName('');
                        toast.success(`Collection "${col.name}" created`);
                      }
                    }}
                    placeholder="New collection..."
                    className="px-2 py-0.5 text-xs border rounded w-32 focus:ring-1 focus:ring-sky-300"
                    data-testid="new-collection-input"
                  />
                </div>
              </div>
              )}

              {loading ? (
                <div className="space-y-2" data-testid="learn-loading">
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} className="h-14 w-full rounded-xl" />
                  ))}
                </div>
              ) : isArchiveView ? (
                <LitArchiveTable
                  articles={articles}
                  onMoveToFolder={handleAddToFolder}
                  onOpenShelf={handleOpenOnShelf}
                />
              ) : isArticleRoute && focusedPmid && !articles.some(a => a.pmid === focusedPmid) ? (
                <div className="text-center py-16" data-testid="learn-article-not-found">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-800 mb-2">This article isn't in your LitHub yet.</h3>
                  <p className="text-sm text-slate-500 mb-4">PMID {focusedPmid}</p>
                  <button onClick={() => navigate(litHubShelf)} className="text-sm text-sky-600 hover:text-sky-700 font-medium">
                    ← Back to your library
                  </button>
                </div>
              ) : filteredArticles.length > 0 ? (
                <div className="space-y-3" data-testid="learn-articles">
                  {searchQuery && (
                    <p className="text-sm text-slate-500 mb-3">
                      {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} matching &ldquo;{searchQuery}&rdquo;
                    </p>
                  )}
                  {filteredArticles.map((article, articleIndex) => (
                    <LibraryArticleCard
                      key={article.pmid}
                      article={article}
                      index={articleIndex}
                      audioStatus={audioStatuses[article.pmid]?.status || 'missing'}
                      audioUrl={audioStatuses[article.pmid]?.audio_url}
                      onAudioSummary={handleAudioSummary}
                      onPlayAudio={async (pmid: string, url: string) => {
                        if (url) {
                          setAudioPlayerPmid(pmid);
                          setAudioPlayerUrl(url);
                          return;
                        }
                        try {
                          const status = await articlesAPI.getAudioSummary(pmid);
                          if (status?.status === 'ready' && status.audio_url) {
                            setAudioStatuses((prev) => ({
                              ...prev,
                              [pmid]: { status: 'ready', audio_url: status.audio_url },
                            }));
                            setAudioPlayerPmid(pmid);
                            setAudioPlayerUrl(status.audio_url);
                          }
                        } catch {
                          toast.error('Could not load audio');
                        }
                      }}
                      onRetryAudio={handleRetryAudio}
                      onDeepDive={handleDeepDive}
                      onDiscuss={handleDiscuss}
                      onSearchFullText={handleSearchFullText}
                      onRemove={handleRemoveArticle}
                      onAddToFolder={handleAddToFolder}
                      onViewInArchive={handleViewInArchive}
                      onRead={markAsRead}
                      isRead={readArticles.has(article.pmid)}
                      collections={userCollections}
                    />
                  ))}
                </div>
              ) : selectedProfileId && !searchQuery ? (
                <div className="text-center py-16" data-testid="learn-empty-digest">
                  <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-800 mb-2">No articles in this digest</h3>
                  <p className="text-sm text-slate-500">Save articles from LitScreen to see them here.</p>
                  {lastDigestRunLabel && (
                    <p className="text-sm text-slate-400 mt-2" data-testid="learn-empty-digest-last-run">
                      Last digest run: {lastDigestRunLabel}
                    </p>
                  )}
                </div>
              ) : searchQuery ? (
                <div className="text-center py-16" data-testid="learn-no-results">
                  <Search className="w-12 h-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4 opacity-50" />
                  <h3 className="font-medium text-[hsl(var(--foreground))] mb-2">No matching articles</h3>
                  <p className="text-sm text-[hsl(var(--muted-foreground))]">Try different search terms</p>
                </div>
              ) : (
                <div className="text-center py-16" data-testid="learn-empty">
                  <BookOpen className="w-16 h-16 text-[hsl(var(--muted-foreground))] mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-[hsl(var(--foreground))] mb-2">Your LitHub is empty</h3>
                  <p className="text-[hsl(var(--muted-foreground))] mb-6 max-w-md mx-auto">Save articles from Search or Screen to build your personal knowledge base.</p>
                  <div className="flex gap-3 justify-center">
                    <Button onClick={() => navigate('/search')}>Search Literature</Button>
                    <Button variant="outline" onClick={() => navigate('/screen')}>Screen Articles</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {audioPlayerPmid && audioPlayerUrl && (
          <div className="border-t border-sky-200 bg-sky-50 px-4 py-2 shrink-0" data-testid="library-audio-player">
            <div className="max-w-4xl mx-auto flex items-center gap-3">
              <Headphones className="h-4 w-4 text-sky-600 shrink-0" />
              <audio controls autoPlay className="flex-1 h-8" src={audioPlayerUrl}>
                Your browser does not support audio.
              </audio>
              <button onClick={() => { setAudioPlayerPmid(null); setAudioPlayerUrl(null); }} className="text-slate-400 hover:text-slate-600" data-testid="close-audio-player">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      <FolderSelectDialog
        open={folderDialogOpen}
        onOpenChange={setFolderDialogOpen}
        onSelect={handleConfirmFolder}
        articleTitle={folderTarget?.title}
      />
    </div>
  );
};

export default LearnWorkspacePage;
